import { useLayoutEffect, useRef } from 'react';
import type { GlossToken } from '../lib/types';

interface GlossStripProps {
  tokens: GlossToken[];
  current: number;
  onSeek(index: number): void;
}

/**
 * The gloss strip is the reply, so it is exposed as a list with the active
 * token marked `aria-current`. Tapping a token pauses playback and pins the
 * stage to that sign — the core learner gesture.
 */
export function GlossStrip({ tokens, current, onSeek }: GlossStripProps) {
  const band = useRef<HTMLOListElement>(null);
  const active = useRef<HTMLButtonElement>(null);

  // Centre the active token in the band on every index change. scrollTop is set
  // directly so the page around the band never moves.
  useLayoutEffect(() => {
    const strip = band.current;
    const token = active.current;
    if (!strip || !token) return;

    const target = token.offsetTop - (strip.clientHeight - token.offsetHeight) / 2;
    const furthest = Math.max(0, strip.scrollHeight - strip.clientHeight);
    strip.scrollTop = Math.max(0, Math.min(target, furthest));
  }, [current, tokens]);

  return (
    <div className="gloss">
      <p className="kicker gloss-kicker">Auslan gloss · the reply</p>
      <ol className="gloss-band" ref={band} aria-label="Auslan gloss — the reply">
        {tokens.map((token, index) => {
          const state = index === current ? 'current' : index < current ? 'done' : 'upcoming';
          return (
            <li key={`${index}-${token.sign}`}>
              <button
                type="button"
                className="gloss-token"
                data-state={state}
                aria-current={index === current ? 'true' : undefined}
                ref={index === current ? active : undefined}
                onClick={() => onSeek(index)}
              >
                {token.sign}
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
